/**
 * useScrollReveal — Composable for scroll-triggered reveal animations
 * Uses native IntersectionObserver (no external libraries).
 *
 * Usage:
 *   import { useScrollReveal } from '@/composables/useScrollReveal'
 *
 *   // In setup:
 *   const { observe, observeAll } = useScrollReveal()
 *
 *   onMounted(() => {
 *     observeAll('.reveal')          // observe every .reveal element
 *     observe(myRef.value)           // observe a single ref
 *   })
 *
 * Elements receive the class `revealed` when they enter the viewport.
 * Pair with CSS classes in variables.css (.reveal, .reveal-left, etc.)
 */

import { onBeforeUnmount } from 'vue'

export function useScrollReveal (options = {}) {
  const {
    threshold = 0.12,
    rootMargin = '0px 0px -40px 0px',
    once = true // only reveal once by default
  } = options

  let observer = null
  const observed = new Set()

  const isMobile = () => window.innerWidth < 768

  const createObserver = () => {
    if (observer) return observer
    if (typeof IntersectionObserver === 'undefined') return null

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed')
          if (once) {
            observer.unobserve(entry.target)
            observed.delete(entry.target)
          }
        } else if (!once) {
          entry.target.classList.remove('revealed')
        }
      })
    }, { threshold, rootMargin })

    return observer
  }

  /**
   * Observe a single DOM element.
   */
  const observe = (el) => {
    if (!el) return

    // On mobile, reveal immediately without animation
    if (isMobile()) {
      el.classList.add('revealed')
      return
    }

    const obs = createObserver()
    if (!obs) {
      el.classList.add('revealed')
      return
    }

    obs.observe(el)
    observed.add(el)
  }

  /**
   * Observe multiple elements. Accepts a CSS selector string OR an iterable of DOM elements (NodeList, Array, etc.).
   * When a string is passed, querySelectorAll is used on the optional root (defaults to document).
   */
  const observeAll = (selectorOrElements, root) => {
    let elements
    if (typeof selectorOrElements === 'string') {
      const container = root || document
      elements = container.querySelectorAll(selectorOrElements)
    } else {
      elements = selectorOrElements
    }
    if (elements && elements.forEach) {
      elements.forEach((el) => observe(el))
    }
  }

  /**
   * Stop observing everything and disconnect.
   */
  const cleanup = () => {
    if (observer) {
      observed.forEach((el) => observer.unobserve(el))
      observer.disconnect()
      observer = null
    }
    observed.clear()
  }

  // Auto-cleanup when component unmounts
  onBeforeUnmount(() => {
    cleanup()
  })

  return { observe, observeAll, cleanup }
}
